/**
 * Brazilian Currency and Locale Utilities
 * For the Brazilian Investor Demo
 */

export type Locale = 'pt-BR' | 'en-US';
export type Currency = 'BRL' | 'USD';

/**
 * Format currency values for Brazilian locale (BRL)
 * Examples: R$ 1.234,56 | R$ 15.432,50
 */
export function formatBRL(amount: number): string {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(amount);
}

/**
 * Format currency with locale awareness
 */
export function formatCurrencyLocale(
  amount: number,
  locale: Locale = 'pt-BR',
  currency: Currency = 'BRL'
): string {
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
  }).format(amount);
}

/**
 * Format percentage for Brazilian locale
 * Example: 18,00%
 */
export function formatPercentBR(rate: number): string {
  return new Intl.NumberFormat('pt-BR', {
    style: 'percent',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(rate / 100);
}

/**
 * Format date for Brazilian locale
 * Example: 15/01/2026
 */
export function formatDateBR(
  date: Date | string,
  options?: Intl.DateTimeFormatOptions
): string {
  const defaultOptions: Intl.DateTimeFormatOptions = {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  };
  return new Intl.DateTimeFormat('pt-BR', options ?? defaultOptions).format(
    new Date(date)
  );
}

/**
 * Format date with full month name in Portuguese
 * Example: 15 de janeiro de 2026
 */
export function formatDateBRLong(date: Date | string): string {
  return new Intl.DateTimeFormat('pt-BR', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  }).format(new Date(date));
}

/**
 * Format CNPJ (Brazilian Company Tax ID)
 * Input: 12345678000199 | Output: 12.345.678/0001-99
 */
export function formatCNPJ(cnpj: string): string {
  const cleaned = cnpj.replace(/\D/g, '');
  if (cleaned.length !== 14) return cnpj;
  return cleaned.replace(
    /^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/,
    '$1.$2.$3/$4-$5'
  );
}

/**
 * Format CPF (Brazilian Individual Tax ID)
 * Input: 12345678901 | Output: 123.456.789-01
 */
export function formatCPF(cpf: string): string {
  const cleaned = cpf.replace(/\D/g, '');
  if (cleaned.length !== 11) return cpf;
  return cleaned.replace(/^(\d{3})(\d{3})(\d{3})(\d{2})$/, '$1.$2.$3-$4');
}

/**
 * Format CEP (Brazilian postal code)
 * Input: 01310100 | Output: 01310-100
 */
export function formatCEP(cep: string): string {
  const cleaned = cep.replace(/\D/g, '');
  if (cleaned.length !== 8) return cep;
  return cleaned.replace(/^(\d{5})(\d{3})$/, '$1-$2');
}

/**
 * Format phone number for Brazil
 * Input: 11999991234 | Output: (11) 99999-1234
 */
export function formatPhoneBR(phone: string): string {
  const cleaned = phone.replace(/\D/g, '');
  if (cleaned.length === 11) {
    return cleaned.replace(/^(\d{2})(\d{5})(\d{4})$/, '($1) $2-$3');
  }
  if (cleaned.length === 10) {
    return cleaned.replace(/^(\d{2})(\d{4})(\d{4})$/, '($1) $2-$3');
  }
  return phone;
}

/**
 * Validate CNPJ check digits
 */
export function validateCNPJ(cnpj: string): boolean {
  const cleaned = cnpj.replace(/\D/g, '');
  if (cleaned.length !== 14) return false;
  if (/^(\d)\1+$/.test(cleaned)) return false;

  const calc = (digits: string, positions: number[]): number => {
    let sum = 0;
    for (let i = 0; i < positions.length; i++) {
      sum += parseInt(digits.charAt(i) || '0', 10) * (positions[i] ?? 0);
    }
    const remainder = sum % 11;
    return remainder < 2 ? 0 : 11 - remainder;
  };

  const pos1 = [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
  const pos2 = [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];

  const digit1 = calc(cleaned, pos1);
  const digit2 = calc(cleaned.slice(0, 12) + digit1, pos2);

  return (
    parseInt(cleaned.charAt(12) || '0', 10) === digit1 && parseInt(cleaned.charAt(13) || '0', 10) === digit2
  );
}

/**
 * Format NF-e access key (44 digits)
 * Adds spaces every 4 digits for readability
 */
export function formatNFeAccessKey(key: string): string {
  const cleaned = key.replace(/\D/g, '');
  if (cleaned.length !== 44) return key;
  return cleaned.match(/.{1,4}/g)?.join(' ') ?? key;
}

/**
 * Calculate time saved display
 * Returns formatted string like "4 min 30 seg"
 */
export function formatTimeSaved(seconds: number): string {
  const minutes = Math.floor(seconds / 60);
  const secs = seconds % 60;
  if (minutes === 0) return `${secs} seg`;
  if (secs === 0) return `${minutes} min`;
  return `${minutes} min ${secs} seg`;
}

/**
 * Brazilian states for address selection
 */
export const BRAZILIAN_STATES = [
  { code: 'AC', name: 'Acre' },
  { code: 'AL', name: 'Alagoas' },
  { code: 'AP', name: 'Amapá' },
  { code: 'AM', name: 'Amazonas' },
  { code: 'BA', name: 'Bahia' },
  { code: 'CE', name: 'Ceará' },
  { code: 'DF', name: 'Distrito Federal' },
  { code: 'ES', name: 'Espírito Santo' },
  { code: 'GO', name: 'Goiás' },
  { code: 'MA', name: 'Maranhão' },
  { code: 'MT', name: 'Mato Grosso' },
  { code: 'MS', name: 'Mato Grosso do Sul' },
  { code: 'MG', name: 'Minas Gerais' },
  { code: 'PA', name: 'Pará' },
  { code: 'PB', name: 'Paraíba' },
  { code: 'PR', name: 'Paraná' },
  { code: 'PE', name: 'Pernambuco' },
  { code: 'PI', name: 'Piauí' },
  { code: 'RJ', name: 'Rio de Janeiro' },
  { code: 'RN', name: 'Rio Grande do Norte' },
  { code: 'RS', name: 'Rio Grande do Sul' },
  { code: 'RO', name: 'Rondônia' },
  { code: 'RR', name: 'Roraima' },
  { code: 'SC', name: 'Santa Catarina' },
  { code: 'SP', name: 'São Paulo' },
  { code: 'SE', name: 'Sergipe' },
  { code: 'TO', name: 'Tocantins' },
] as const;

export type BrazilianState = (typeof BRAZILIAN_STATES)[number]['code'];
